/**
 * The queue filters behind one "Filters (n)" button, at every width (minimal redesign,
 * 12 Oct 2026). A right drawer holds what the filter row used to show: assignee,
 * mailbox (with the scope line), area, sender, type, received dates, the four
 * switches and "Back to my default view". Every control writes the same URL params as
 * before (`queue-defaults.ts`); the sheet owns no filter state of its own.
 */

import { FilterX, Icon } from "@norican/icons";
import {
  Badge,
  Button,
  DateRangePicker,
  Drawer,
  FilterSelect,
  Select,
  Switch,
  Typography,
} from "@norican/react";
import type { ComponentProps } from "react";

import {
  ASSIGNEE_OPTIONS,
  assigneeOptionsFor,
  toAssignee,
  TODAY_DAYS,
} from "@/features/queue/queue-defaults";
import { type QueueAssignee, type QueueView } from "@/lib/api";
import { FEATURES } from "@/lib/features";
import { intentOptionsFor } from "@/lib/intents";
import { logger } from "@/lib/logger";
import { CLOSE_OFFER_WORDS } from "@/lib/sales-flow";
import { SENDER_OPTIONS } from "@/lib/work-labels";

const nf = new Intl.NumberFormat(undefined);

const ASSIGNEE_LABEL = Object.fromEntries(
  ASSIGNEE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<QueueAssignee, string>;

/**
 * The switch sales use to drop mails a customer wrote to a colleague by name; it was
 * "Addressed to me or nobody" until the invitee review (MK-07, 12 Oct 2026). The URL
 * param `addressed=me_or_nobody` is unchanged.
 */
export const ADDRESSED_SWITCH_LABEL = "Only mails to the shared mailbox or to me";

/** "Filters" with the number of filters set beyond the default view: "Filters (2)". */
export function filtersButtonLabel(count: number): string {
  return count > 0 ? `Filters (${nf.format(count)})` : "Filters";
}

/** "All 10 mailboxes, 7 areas. Pick an area to narrow." under the mailbox picker (SA-02). */
export function scopeLineText(mailboxCount: number, areaCount: number): string {
  const mailboxes = `${nf.format(mailboxCount)} ${mailboxCount === 1 ? "mailbox" : "mailboxes"}`;
  const areas =
    areaCount > 0 ? `, ${nf.format(areaCount)} ${areaCount === 1 ? "area" : "areas"}` : "";
  return `All ${mailboxes}${areas}. Pick an area to narrow.`;
}

/** "Today" or "Last 7 days": the relative window a URL's `days` stands for. */
export function daysBadgeText(days: number): string {
  return days === TODAY_DAYS ? "Today" : `Last ${days} ${days === 1 ? "day" : "days"}`;
}

type DateRangeValue = ComponentProps<typeof DateRangePicker>["value"];

export interface FiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The roles of the viewer: a manager gets no "Mine and unassigned". */
  roles: readonly string[] | undefined;
  /** The viewer quotes (sales): only they have mails "to me" (MA-07). */
  quotes: boolean;
  view: QueueView;
  assignee: QueueAssignee;
  onAssigneeChange: (value: QueueAssignee) => void;
  mailboxOptions: { value: string; label: string }[];
  mailboxes: string[];
  onMailboxesChange: (values: string[]) => void;
  /** The mailboxes and areas of the viewer's scope, for the scope line (0 hides it). */
  scopeMailboxCount: number;
  scopeAreaCount: number;
  areaOptions: { value: string; label: string; searchText?: string }[];
  areas: string[];
  onAreasChange: (values: string[]) => void;
  senders: string[];
  onSendersChange: (values: string[]) => void;
  intents: string[];
  onIntentsChange: (values: string[]) => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  /** The relative window of the URL, or null for an explicit range or any date. */
  days: number | null;
  addressedToMe: boolean;
  onAddressedToMeChange: (checked: boolean) => void;
  /**
   * Hunt round 2, decision 8: only the cases that offer "Close: not an inquiry" (a system
   * notification or a newsletter). Optional, so an older caller shows no switch.
   */
  closeOffer?: boolean;
  onCloseOfferChange?: (checked: boolean) => void;
  hideColleagues: boolean;
  onHideColleaguesChange: (checked: boolean) => void;
  showReplies: boolean;
  onShowRepliesChange: (checked: boolean) => void;
  includeUnmapped: boolean;
  onIncludeUnmappedChange: (checked: boolean) => void;
  /** True when anything differs from the default view: "Back to my default view" is offered. */
  filtersActive: boolean;
  onReset: () => void;
}

export function FiltersSheet(props: FiltersSheetProps) {
  const typeOptions = intentOptionsFor(props.view);
  return (
    <Drawer.Root open={props.open} onOpenChange={props.onOpenChange} side="right">
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup aria-label="Queue filters" data-queue-filters>
            <Drawer.Header>
              <Drawer.Title>Filters</Drawer.Title>
              <Drawer.CloseButton label="Close the filters" />
            </Drawer.Header>
            <Drawer.Body>
              <div className="flex flex-col gap-4">
                <Field label="Assigned to">
                  <Select.Root
                    value={props.assignee}
                    onValueChange={(value) => {
                      const next = toAssignee(typeof value === "string" ? value : null);
                      logger.debug("[queue] assignee", next);
                      props.onAssigneeChange(next);
                    }}
                  >
                    <Select.Trigger size="sm" aria-label="Assigned to" className="w-full">
                      <Select.Value>
                        {(value: unknown) => ASSIGNEE_LABEL[toAssignee(String(value))]}
                      </Select.Value>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Positioner>
                        <Select.Popup>
                          <Select.List>
                            {assigneeOptionsFor(props.roles).map((option) => (
                              <Select.Item key={option.value} value={option.value}>
                                {option.label}
                              </Select.Item>
                            ))}
                          </Select.List>
                        </Select.Popup>
                      </Select.Positioner>
                    </Select.Portal>
                  </Select.Root>
                </Field>

                <Field label="Mailbox">
                  <FilterSelect
                    placeholder="All my mailboxes"
                    size="sm"
                    options={props.mailboxOptions}
                    value={props.mailboxes}
                    onValueChange={props.onMailboxesChange}
                  />
                  {props.scopeMailboxCount > 1 && (
                    <Typography as="span" variant="caption" tone="muted">
                      {scopeLineText(props.scopeMailboxCount, props.scopeAreaCount)}
                    </Typography>
                  )}
                </Field>

                {props.areaOptions.length > 0 && (
                  <Field label="Area">
                    <FilterSelect
                      noun="area"
                      placeholder="Area"
                      size="sm"
                      searchable={false}
                      options={props.areaOptions}
                      value={props.areas}
                      onValueChange={props.onAreasChange}
                    />
                  </Field>
                )}

                <Field label="Sender">
                  <FilterSelect
                    noun="sender"
                    placeholder="Sender"
                    size="sm"
                    searchable={false}
                    options={SENDER_OPTIONS.map((option) => ({ ...option }))}
                    value={props.senders}
                    onValueChange={props.onSendersChange}
                  />
                </Field>

                {/* A filter with one choice filters nothing: the working tabs hold inquiries only. */}
                {typeOptions.length > 1 && (
                  <Field label="Type">
                    <FilterSelect
                      noun="type"
                      placeholder="Type"
                      size="sm"
                      searchable={false}
                      options={typeOptions.map((option) => ({ ...option }))}
                      value={props.intents}
                      onValueChange={props.onIntentsChange}
                    />
                  </Field>
                )}

                <div className="flex flex-col gap-2">
                  {/* The picker's own label names the date group, so a screen reader says
                      "month, Start Date, Received" instead of every segment of both dates. */}
                  <DateRangePicker
                    size="sm"
                    label="Received"
                    value={props.dateRange}
                    onValueChange={props.onDateRangeChange}
                  />
                  {props.days != null && (
                    <Badge tone="neutral" variant="outline" size="sm" className="w-fit">
                      {daysBadgeText(props.days)}
                    </Badge>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  {props.quotes && (
                    // Only a sales person has mails "to me"; a manager reads the whole area (MA-07).
                    <Switch.Root
                      checked={props.addressedToMe}
                      onCheckedChange={props.onAddressedToMeChange}
                      label={
                        <span
                          className="text-fg-muted text-xs"
                          title="Leaves out mails the customer wrote to a colleague by name. Mails to the shared mailbox only stay."
                        >
                          {ADDRESSED_SWITCH_LABEL}
                        </span>
                      }
                    />
                  )}
                  {props.onCloseOfferChange && (
                    <Switch.Root
                      checked={props.closeOffer === true}
                      onCheckedChange={props.onCloseOfferChange}
                      label={
                        <span
                          className="text-fg-muted text-xs"
                          title="Only system notifications and newsletters that offer the close. Nothing closes by itself."
                        >
                          {CLOSE_OFFER_WORDS.filter}
                        </span>
                      }
                      data-close-offer-filter
                    />
                  )}
                  <Switch.Root
                    checked={props.hideColleagues}
                    onCheckedChange={props.onHideColleaguesChange}
                    label={
                      <span className="text-fg-muted text-xs">Hide colleagues&apos; mails</span>
                    }
                  />
                  {FEATURES.groupThreads && (
                    <Switch.Root
                      checked={props.showReplies}
                      onCheckedChange={props.onShowRepliesChange}
                      label={
                        <span className="text-fg-muted text-xs">
                          Show replies as their own rows
                        </span>
                      }
                    />
                  )}
                  <Switch.Root
                    checked={props.includeUnmapped}
                    onCheckedChange={props.onIncludeUnmappedChange}
                    label={
                      <span className="text-fg-muted text-xs">
                        Include mailboxes without a live item catalogue
                      </span>
                    }
                  />
                </div>
              </div>
            </Drawer.Body>
            {props.filtersActive && (
              <Drawer.Footer>
                <Button
                  variant="ghost"
                  tone="neutral"
                  size="sm"
                  startIcon={<Icon icon={FilterX} size="sm" />}
                  onClick={props.onReset}
                >
                  Back to my default view
                </Button>
              </Drawer.Footer>
            )}
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/** A label over one filter control, so the sheet reads as a form. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Typography as="span" variant="label">
        {label}
      </Typography>
      {children}
    </div>
  );
}
